import { useEffect, useRef } from "react";

import { URLHandler } from "src/services/URLHandler";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useGetAllActionPlanActions } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";

import { usePuckActionPlan } from "../../../contexts/PuckActionPlanContext";
import { usePuckCompanyScore } from "../../../contexts/PuckCompanyScoreContext";
import { extractYearFromLabel, calculatePercentageDifference } from "../utils/formatters";
import { buildMaxScoreArray } from "src/utils/chart";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { OlapHeadingColorService } from "src/context/OlapScores/OlapHeadingColorService";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { OlapLocalizedLabelService } from "src/context/OlapScores/OlapLocalizedLabelService";
import { DimensionValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export interface ActionPlanDataProps {
    actionPlanId?: string;
    scoreId?: number | null;
    variationId?: number | null;
    customGoalValue?: number;
    customGoalYear?: number;
    customGoalUnit?: string;
}

export const useActionPlanData = (options: ActionPlanDataProps) => {
    const { actionPlanId, scoreId, variationId, customGoalValue, customGoalYear, customGoalUnit } = options;
    const { isFeatureActivated } = useApplicationParameters();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const {
        actionPlanData,
        isLoading: isActionPlanLoading,
        setVariation: setActionPlanVariation,
    } = usePuckActionPlan();
    const { companyPost, isLoading: isCompanyLoading, setVariation, scoreTitle } = usePuckCompanyScore();

    const chosenActionPlan = actionPlanData.find((planData) => planData.scoreId === scoreId);
    const ssqVersionId = chosenActionPlan?.metadata.startingPost.ssqVersionId;
    const { ssqVersion, isReady: isSsqVersionReady } = useGetOneSsqVersion(ssqVersionId);

    const lastAppliedVariationRef = useRef<{ scoreId: number; variationId: number | null } | null>(null);

    useEffect(() => {
        if (typeof scoreId !== "number") {
            return;
        }

        if (variationId === undefined) {
            return;
        }

        const normalizedVariationId = variationId ?? null;

        const previous = lastAppliedVariationRef.current;
        if (previous?.scoreId === scoreId && previous.variationId === normalizedVariationId) {
            return;
        }

        lastAppliedVariationRef.current = { scoreId, variationId: normalizedVariationId };

        setVariation(scoreId, normalizedVariationId);
        setActionPlanVariation(scoreId, normalizedVariationId);
    }, [scoreId, setActionPlanVariation, setVariation, variationId]);

    const isLoading = isActionPlanLoading || isCompanyLoading || !isSsqVersionReady;
    const hasError = !isLoading && (!companyPost || (!actionPlanData.length && actionPlanId));

    const activePlan = actionPlanData.find((plan) => {
        if (actionPlanId && plan.metadata?.actionPlan?.id === actionPlanId) {
            return true;
        }

        const scoreMatches = scoreId && plan.scoreId === scoreId;
        if (scoreMatches) {
            return true;
        }

        return false;
    });

    // Utiliser l'ID du plan actif trouvé, ou l'actionPlanId fourni en props
    const effectiveActionPlanId = activePlan?.metadata?.actionPlan?.id || actionPlanId;

    // Récupérer les actions du plan d'action pour compter le nombre d'actions
    const { data: allActionPlanActions } = useGetAllActionPlanActions({
        actionPlanID: effectiveActionPlanId,
    });

    const diagnosticScoreFromCompany = companyPost?.olapScores && scoreId ? companyPost.olapScores[scoreId] : undefined;
    const diagnosticScore = (diagnosticScoreFromCompany?.score.value as number) || 0;
    const diagnosticYearValue = extractYearFromLabel(companyPost?.campaign?.label);
    const diagnosticSSQCompletions = companyPost?.completions?.ssq.result || 0;

    const filteredScoreItems =
        scoreId ?
            activePlan?.metadata?.scoresWithActionPost?.details.filter((item) => {
                return item.score2 && "scoreId" in item.score2 && item.score2.scoreId === scoreId;
            })
        :   [];

    const projectedScoreItem =
        filteredScoreItems && filteredScoreItems.length > 0 ?
            filteredScoreItems[filteredScoreItems.length - 1]
        :   activePlan?.metadata?.scoresWithActionPost?.lastItem;

    const matchingScoreDetail = activePlan?.metadata?.scoresWithActionPost?.details?.find(
        (detail) => scoreId && detail.score2 && "id" in detail.score2 && detail.score2.id === scoreId,
    );

    const projectedRawScore =
        matchingScoreDetail?.score ||
        activePlan?.metadata?.scoresWithActionPost?.lastItem?.score ||
        (scoreId ? diagnosticScoreFromCompany?.score.value : null) ||
        0;

    const projectedScore = OlapScoresService.normalizeNumericalScore(projectedRawScore) ?? 0;
    const projectedYearLabel = projectedScoreItem?.campaign?.label || "";
    const projectedYearValue = extractYearFromLabel(projectedYearLabel, diagnosticYearValue);

    const goalValue = customGoalValue !== undefined ? customGoalValue : projectedScore;
    const goalYear = customGoalYear !== undefined ? customGoalYear : projectedYearValue;
    const goalUnit = customGoalUnit || diagnosticScoreFromCompany?.unit || "";

    // Calculer le nombre d'actions dans le plan d'action
    const actionCount = allActionPlanActions?.length || 0;
    const unit = diagnosticScoreFromCompany?.unit || "";

    const diagnosticToProjectedDifference = calculatePercentageDifference(projectedScore, diagnosticScore);
    const projectedToGoalDifference = calculatePercentageDifference(
        OlapScoresService.normalizeNumericalScore(goalValue, 0),
        OlapScoresService.normalizeNumericalScore(projectedScore, 0),
    );
    const diagnosticToGoalDifference = calculatePercentageDifference(
        OlapScoresService.normalizeNumericalScore(goalValue, 0),
        OlapScoresService.normalizeNumericalScore(diagnosticScore, 0),
    );

    const diagnosticHeadingsScoresDimension = OlapScoresService.getDimension(
        "headings",
        diagnosticScoreFromCompany?.dimensions,
    );
    const projectedHeadingsScoresDimension = OlapScoresService.getDimension(
        "headings",
        projectedScoreItem?.score2.dimensions,
    );

    const diagnosticSubHeadings = ((): DimensionValue[] | undefined => {
        const sub =
            diagnosticScoreFromCompany?.subscores ? Object.values(diagnosticScoreFromCompany.subscores)[0] : undefined;
        const values = (sub?.dimensions?.[0]?.values ?? undefined) as DimensionValue[] | undefined;
        return values;
    })();
    const projectedSubHeadings = ((): DimensionValue[] | undefined => {
        const sub =
            projectedScoreItem?.score2?.subscores ? Object.values(projectedScoreItem.score2.subscores)[0] : undefined;
        const values = (sub?.dimensions?.[0]?.values ?? undefined) as DimensionValue[] | undefined;
        return values;
    })();

    const diagnosticScoresFlattenedHeadings = OlapScoresService.flattenDimensionValues(
        diagnosticHeadingsScoresDimension,
    );
    const projectedScoresFlattenedHeadings = OlapScoresService.flattenDimensionValues(projectedHeadingsScoresDimension);

    const localizeHeading = (value: { id: string; label: string }): string =>
        OlapLocalizedLabelService.localize("headings", value.id, { ssqVersion }) ?? value.label;

    const resolveHeadingColors = (values: Array<{ id: string }>): string[] =>
        OlapHeadingColorService.resolveMany(
            values.map((value) => ({ dimension: "headings", id: value.id })),
            { ssqVersion },
        );

    const diagnosticScoreChart = {
        labels: diagnosticScoresFlattenedHeadings.map(localizeHeading),
        values: diagnosticScoresFlattenedHeadings.map((value) =>
            OlapScoresService.normalizeNumericalScore(value.score, 0),
        ),
        colors: resolveHeadingColors(diagnosticScoresFlattenedHeadings),
    };

    const projectedScoreChart = {
        labels: projectedScoresFlattenedHeadings.map(localizeHeading),
        values: projectedScoresFlattenedHeadings.map((value) =>
            OlapScoresService.normalizeNumericalScore(value.score, 0),
        ),
        colors: resolveHeadingColors(projectedScoresFlattenedHeadings),
    };

    const legendItems = diagnosticScoresFlattenedHeadings.map((value, index) => ({
        color: diagnosticScoreChart.colors[index],
        label: localizeHeading(value),
    }));

    // NOTE: kept as raw OLAP labels on purpose — `buildMaxScoreArray` (src/utils/chart.ts)
    // indexes `subHeadings` by their raw `label` string. Localizing here would silently
    // return 0 for every max-score lookup. Display-only label arrays are localized above.
    const labels = diagnosticScoresFlattenedHeadings.map((value) => value.label);

    const diagnosticCTAURL =
        selectedCompany && currentCampaign?.id ?
            URLHandler.company.questionnaireScore(
                selectedCompany.id,
                selectedCompany.organizationSector?.ssqId ?? -99,
                currentCampaign?.id,
                scoreId?.toString() ?? "-99",
            )
        :   "";
    const projectedCTAURL =
        isFeatureActivated("FS-actionPlan") && selectedCompany?.id ?
            URLHandler.company.overview(selectedCompany.id)
        :   "";

    return {
        isLoading,
        hasError,

        scoreTitle,

        diagnosticScore,
        diagnosticScoreChart,
        diagnosticMaxScore: buildMaxScoreArray(labels, diagnosticSubHeadings),
        diagnosticYearValue,
        diagnosticSSQCompletions,
        diagnosticCTAURL,

        projectedScore,
        projectedScoreChart,
        projectedMaxScore: buildMaxScoreArray(labels, projectedSubHeadings),
        projectedYearValue,
        actionCount,
        projectedCTAURL,

        goalValue,
        goalYear,
        goalUnit,

        diagnosticToProjectedDifference,
        projectedToGoalDifference,
        diagnosticToGoalDifference,

        unit,
        legendItems,
    };
};
